<script context="module" lang="ts">
    import type { EditorLayout, TabLayout, SubLayoutState } from "../editor";
    import { get, type Writable } from "svelte/store";

    export function createTabPanelLayout(id: string, layout: EditorLayout) {
        const tabLayout = layout.subLayouts[id] as TabLayout & { state: Writable<SubLayoutState> };
        if (!tabLayout) throw new Error(`No split panel layout with id '${id}' found.`);

        let { initial, initialOpen, position, closable } = tabLayout;
        closable ??= true;
        const state = layout.state.create(id, { tabs: initial, active: initialOpen, open: true });

        function active() {
            const s = get(state);
            return s.active;
        }

        function toggle(item: string) {
            state.update((state) => {
                let index = state.tabs.indexOf(item);
                if (index === -1) {
                    state.tabs.push(item);
                    index = state.tabs.length - 1;
                }

                if (state.active === item) {
                    state.open = !state.open || !closable;
                } else {
                  state.open = true;
                }

                state.active = item;
                return state;
            });
        }

        function open(item: string) {
            state.update((state) => {
                let index = state.tabs.indexOf(item);
                if (index === -1) {
                    state.tabs.push(item);
                    index = state.tabs.length - 1;
                }
                state.active = item;
                state.open = true;
                return state;
            });
        }

        function close(item: string) {
            state.update((state) => {
                if (state.active !== item) return state;

                let index = state.tabs.indexOf(item);
                if (index === -1) index = 0;
                state.active = state.tabs[Math.max(index - 1, 0)];
                state.open = false || !closable;

                return state;
            });
        }

        tabLayout.state.set({
            toggle,
            active,
            open,
            close
        });

        return {
            state,
            position,
            toggle,
        };
    }

</script>

<script lang="ts">
    import { getLayout } from "../Layout.svelte";
    import PanelTemplate from "../PanelTemplate.svelte";
    import { getSubLayout } from "./sublayout";

    const { panel, layout: editorLayout } = getLayout();
    const { collapse } = getSubLayout();

    export let id: string;
    let clazz: string = "";
    export { clazz as class };

    const splitLayout = createTabPanelLayout(id, editorLayout);
    const { state, position, toggle } = splitLayout;

    $: collapse(id, !$state.open);

    const { panels } = editorLayout;
</script>

<!----------------------------------------------------------------------------->

<div class="tab-panel {position} {clazz}" class:closed={!$state.open} style="grid-area: {id}">
  <div class="tabs" role="tablist">
    {#each $state.tabs as tab}
      {@const name = $panels[tab].name}
      <button role="tab" class:active={tab === $state.active} on:click={() => toggle(tab)}>{name}</button>
    {/each}
  </div>

  <div class="content">
    {#each $state.tabs as tab}
      {@const item = panel(tab)}
      {#if item}
        <div class:hidden={tab !== $state.active} role="tabpanel">
          <PanelTemplate panel={item} />
        </div>
      {:else}
        <span class:hidden={tab !== $state.active}>Unknown panel: {tab}</span>
      {/if}
    {/each}
  </div>
</div>

<!----------------------------------------------------------------------------->

<style>
    div.tab-panel {
        display: grid;
        height: 100%;
        width: 100%;
        user-select: none;

        &.bottom {
            grid-template:
          "content" 1fr
          "tabs" max-content;
          & .tabs {
            border-top: 1px solid var(--gap-color);
          }
        }

        &.top {
            grid-template:
          "tabs" max-content
          "content" 1fr;
          & .tabs {
            border-bottom: 1px solid var(--gap-color);
          }
        }

        &.left {
            grid-template:
          "tabs content" 1fr /
          max-content 1fr;
          & .tabs {
            border-right: 1px solid var(--gap-color);
          }
        }

        &.right {
            grid-template:
          "content tabs" 1fr /
          1fr max-content;
          & .tabs {
            border-left: 1px solid var(--gap-color);
          }
        }
    }

    .content {
        grid-area: content;
        overflow: hidden;

        & [role="tabpanel"] {
            height: 100%;
        }
    }


    .closed {
        height: max-content;

        & .content {
            display: none;
        }
    }

    .hidden {
        display: none;
    }

    .tabs {
        grid-area: tabs;
        display: flex;
        flex-direction: row;
        overflow-x: auto;
    }

    button[role="tab"] {
        all: unset;
        font-size: 0.9rem;
        padding: 0.125em 0.5em;
        cursor: pointer;
        border-right: 1px solid var(--gap-color);
        margin: 0;
        flex: 0 0 max-content;

        &:hover {
            background-color: var(--g150);
        }

        &.active {
            color: var(--tacc);
            box-shadow: inset 0 2px var(--gacc100);
        }
    }
</style>
